import { Divider, makeStyles } from '@material-ui/core';
import React from 'react';
import { ArrowRight, Download } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { enumToIcon, enumToStatusType } from '../../../types/common';
import { Project } from '../../../types/project';
import { downloadFileDialog, getContentUrl } from '../../../util/file';
import { Badge, IconButton, StatusCard, StatusIcon } from '../../global';

const useProjectCardClasses = makeStyles({
    root: {
        display: 'flex',
        gap: 5,
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        background: theme.colors.neutral[4],
        '&:first-child': {
            flex: 1,
        },
        '&:last-child:not(:first-child)': {
            justifyContent: 'center',
            padding: 0,
            borderLeft: 'none',
            color: theme.colors.darkBlue[5],
        },
        '& > span': {
            color: theme.colors.darkBlue[5],
            ...theme.typography.content.label.default,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
    },
    badges: {
        display: 'flex',
        gap: 5,
    },
});

/**
 * Render a project card component.
 *
 * @param props.name name of the asset of the project
 * @param props.project project data
 * @param props.download add a download report button
 */
export const ProjectCard = (props: { name: string; project: Project; download?: boolean }) => {
    const classes = useProjectCardClasses();
    const { Trans } = usePrefixTranslation('project', { project: 'common.ProjectCard' });
    const status = props.project.status;
    const start = status !== 'completed' ? props.project.estimated_start_date : props.project.in_progress_date;
    const end = status !== 'completed' ? props.project.estimated_completed_date : props.project.completed_date;

    return (
        <div className={classes.root}>
            <StatusCard className={classes.card} type={enumToStatusType[props.project.status]}>
                <span>{props.name}</span>
                <div className={classes.badges}>
                    {['external', 'combined'].includes(props.project.type) && <Badge type='neutral'>{'ext'}</Badge>}
                    {['internal', 'combined'].includes(props.project.type) && <Badge type='neutral'>{'int'}</Badge>}
                    {props.project.rope_access && <StatusIcon type='neutral' icon='R' />}
                    {props.project.internal_scaffold && <StatusIcon type='neutral' icon='S' />}
                    {props.project.heavy_cleaning && <StatusIcon type='neutral' icon='C' />}
                </div>
                <Divider />
                <StatusIcon
                    type={enumToStatusType[props.project.status]}
                    icon={enumToIcon[props.project.status]}
                    colored
                >
                    <Trans
                        i18nKey='interval'
                        values={{ start, end }}
                        components={{ arrow: <ArrowRight style={{ width: 15, height: 15 }} /> }}
                    />
                </StatusIcon>
            </StatusCard>
            {props.download && props.project.report !== '' && (
                <StatusCard className={classes.card} type={enumToStatusType[props.project.status]}>
                    <IconButton
                        onClick={async () => {
                            const url = await getContentUrl(props.project.report);
                            downloadFileDialog(url, 'report');
                        }}
                    >
                        <Download />
                    </IconButton>
                </StatusCard>
            )}
        </div>
    );
};
